import { useCallback, useEffect, useMemo } from "react";

import { type TaskSamplesView } from "@tsmono/inspect-common/types";

import { useSelectedLogDetails } from "../../../state/hooks";
import { useStore } from "../../../state/store";
import { useCurrentLogFile } from "../../routing/currentSelection";
import { getFieldKey, type PickerColumn } from "../../shared/gridUtils";
import { type WireScoreColorScale } from "../../shared/samples-grid/colorScale";
import { SCORE_FIELD_RAW_PREFIX } from "../../shared/samples-grid/columns";

import { type SamplesViewState } from "./samplesView";
import {
  liftEvalView,
  pickActiveView,
  resolveSamplesView,
} from "./samplesView.converters";

/** Single subscription to the eval-author-supplied `task_samples_view`
 *  descriptor (after picking an active variant from a multi-view list).
 *  Backs every hook that reads from the wire side. */
function useEvalDefaultSamplesView(): TaskSamplesView | undefined {
  const evalDefaultField =
    useSelectedLogDetails()?.eval.viewer?.task_samples_view ?? null;
  return useMemo(() => pickActiveView(evalDefaultField), [evalDefaultField]);
}

/** Single subscription + resolution; backs every hook that needs a
 *  field off the resolved view. Reads the per-log bucket keyed by the
 *  currently-selected log file so customizations don't bleed across logs
 *  with different scorers / eval config. */
function useResolvedSamplesView(): SamplesViewState {
  const logFile = useCurrentLogFile();
  const stored = useStore((state) =>
    logFile ? state.logs.samplesListState.byLog[logFile] : undefined
  );
  const evalDefault = useEvalDefaultSamplesView();
  return useMemo(
    () => resolveSamplesView(stored, evalDefault),
    [stored, evalDefault]
  );
}

/** Just the resolved `multiline` — for callers that need it before
 *  computing `allColumns` (which the full hook requires). */
export function useSamplesViewMultiline(): boolean {
  return useResolvedSamplesView().multiline;
}

// Stable fallbacks: a fresh `{}` per render would invalidate every
// downstream memo (columnDefs, scoreColorScales) and re-fire the
// `redrawRows` effect in SamplesTab on every render — tearing down and
// recreating all grid row DOM each time.
const kNoScoreLabels: Record<string, string> = Object.freeze({});
const kNoScoreColorScales: Record<string, WireScoreColorScale> = Object.freeze(
  {}
);

/** Eval-author-supplied score-label overrides. Read straight from
 *  the wire (no persistence) so a user's stored view from a prior
 *  eval can't shadow the current eval's labels. Returns an empty
 *  object when no overrides are present. */
export function useSamplesViewScoreLabels(): Record<string, string> {
  return useEvalDefaultSamplesView()?.score_labels ?? kNoScoreLabels;
}

/** Eval-author-supplied score-cell colour scales. Same wire-only
 *  access pattern as `useSamplesViewScoreLabels` — never lifted into
 *  the persisted runtime state, so colour scales from a prior eval
 *  can't bleed into the current one. */
export function useSamplesViewScoreColorScales(): Record<
  string,
  WireScoreColorScale
> {
  const evalDefault = useEvalDefaultSamplesView();
  return evalDefault?.score_color_scales ?? kNoScoreColorScales;
}

/** Resolved `colorScalesEnabled` — same access pattern as the
 *  multiline companion above. Read in `SamplesTab` so the columns
 *  builder can decide whether to attach a `cellStyle` callback. */
export function useSamplesViewColorScalesEnabled(): boolean {
  return useResolvedSamplesView().colorScalesEnabled;
}

/** Resolved `compactScores` — same access pattern as the multiline
 *  companion above. Read in `SamplesTab` before columns are built so
 *  the flag can flow into `buildSampleColumns`. */
export function useSamplesViewCompactScores(): boolean {
  return useResolvedSamplesView().compactScores;
}

export interface UseSamplesViewResult {
  view: SamplesViewState;
  columnVisibility: Record<string, boolean>;
  setView: (next: SamplesViewState) => void;
  patchView: (partial: Partial<SamplesViewState>) => void;
  setColumnVisibility: (visibility: Record<string, boolean>) => void;
  setMultiline: (multiline: boolean) => void;
  setCompactScores: (compactScores: boolean) => void;
  setColorScalesEnabled: (enabled: boolean) => void;
  /** Reset just the column list (order + visibility) to the
   *  eval-author default (when present) or the built-in seeded
   *  default. Sort, filter, multiline, and compactScores are left
   *  alone. */
  resetColumns: () => void;
}

/**
 * Window onto the SampleList scope's `TaskSamplesView` descriptor. Resolves
 * stored > eval-author default > built-in default; seeds visibility for
 * unseeded columns.
 *
 * Selected-scores projection (the `score__*` ↔ `selectedScores` split)
 * stays in `SamplesTab` for now.
 */
export function useSamplesView(
  allColumns?: PickerColumn[],
  options?: {
    seedDefaultVisibility?: (col: PickerColumn) => boolean;
  }
): UseSamplesViewResult {
  const { seedDefaultVisibility } = options ?? {};

  const evalDefault = useEvalDefaultSamplesView();
  const view = useResolvedSamplesView();
  const logFile = useCurrentLogFile();
  const setSampleListViewAction = useStore(
    (state) => state.logsActions.setSampleListView
  );

  // Bind the log file at hook level so a pending effect from log A's
  // render can't redirect a write into log B's bucket if navigation
  // races the effect.
  const setSampleListView = useCallback(
    (next: SamplesViewState) => {
      if (!logFile) return;
      setSampleListViewAction(logFile, next);
    },
    [logFile, setSampleListViewAction]
  );

  const columnVisibility = useMemo<Record<string, boolean>>(() => {
    const v: Record<string, boolean> = {};
    for (const col of view.columns) v[col.id] = col.visible;
    return v;
  }, [view.columns]);

  // Seeding writes the resolved view (not just the diff) so eval-author
  // defaults for sort/filter/multiline land in the stored slot the first
  // time we encounter a new column.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (!allColumns || !seedDefaultVisibility) return;
    const known = new Set(view.columns.map((c) => c.id));
    const additions: SamplesViewState["columns"] = [];
    for (const col of allColumns) {
      const id = getFieldKey(col);
      if (!known.has(id)) {
        additions.push({ id, visible: seedDefaultVisibility(col) });
      }
    }
    if (additions.length === 0) return;
    setSampleListView({ ...view, columns: [...view.columns, ...additions] });
  }, [allColumns, view, seedDefaultVisibility, setSampleListView]);

  const patchView = useCallback(
    (partial: Partial<SamplesViewState>) => {
      setSampleListView({ ...view, ...partial });
    },
    [view, setSampleListView]
  );

  const setColumnVisibility = useCallback(
    (visibility: Record<string, boolean>) => {
      const seen = new Set<string>();
      const nextColumns: SamplesViewState["columns"] = [];
      for (const c of view.columns) {
        const nextVisible = visibility[c.id];
        nextColumns.push(
          nextVisible !== undefined ? { id: c.id, visible: nextVisible } : c
        );
        seen.add(c.id);
      }
      for (const [id, visible] of Object.entries(visibility)) {
        if (!seen.has(id)) nextColumns.push({ id, visible });
      }
      patchView({ columns: nextColumns });
    },
    [view.columns, patchView]
  );

  // Toggle setters write the value into `userOverrides` so future renders
  // know the user has explicitly flipped this field. Without that flag,
  // `resolveSamplesView` lets eval-author defaults shadow the stored value.
  const setMultiline = useCallback(
    (multiline: boolean) =>
      patchView({
        multiline,
        userOverrides: { ...view.userOverrides, multiline },
      }),
    [patchView, view.userOverrides]
  );

  // Score columns are sized per mode (narrow and rotated vs. wide), so a
  // width set in one mode would be wrong in the other: the toggle drops
  // score-column widths and keeps every other column's.
  const setCompactScores = useCallback(
    (compactScores: boolean) =>
      patchView({
        compactScores,
        userOverrides: { ...view.userOverrides, compactScores },
        columnWidths:
          view.columnWidths &&
          Object.fromEntries(
            Object.entries(view.columnWidths).filter(
              ([id]) => !id.startsWith(SCORE_FIELD_RAW_PREFIX)
            )
          ),
      }),
    [patchView, view.userOverrides, view.columnWidths]
  );

  const setColorScalesEnabled = useCallback(
    (colorScalesEnabled: boolean) =>
      patchView({
        colorScalesEnabled,
        userOverrides: { ...view.userOverrides, colorScalesEnabled },
      }),
    [patchView, view.userOverrides]
  );

  const setView = useCallback(
    (next: SamplesViewState) => setSampleListView(next),
    [setSampleListView]
  );

  const resetColumns = useCallback(() => {
    if (!allColumns) return;
    // Resolve the eval-author default's column list (if any). Each
    // entry is `{ id, visible }`. Anything in `allColumns` that
    // isn't covered by the eval default falls through to the
    // caller-supplied seed (the same predicate `useEffect` uses to
    // initialize new columns).
    const evalCols = liftEvalView(evalDefault).columns;
    const evalMap = new Map(evalCols.map((c) => [c.id, c.visible]));
    const nextColumns: SamplesViewState["columns"] = allColumns.map((col) => {
      const id = getFieldKey(col);
      if (evalMap.has(id)) return { id, visible: evalMap.get(id)! };
      return {
        id,
        visible: seedDefaultVisibility ? seedDefaultVisibility(col) : true,
      };
    });
    patchView({ columns: nextColumns });
  }, [allColumns, evalDefault, seedDefaultVisibility, patchView]);

  return {
    view,
    columnVisibility,
    setView,
    patchView,
    setColumnVisibility,
    setMultiline,
    setCompactScores,
    setColorScalesEnabled,
    resetColumns,
  };
}
